import { getUniqueVehicleOfInterest, getVehicleName } from "../engagementHelpers";
import {
  buildFieldVariables,
  getMergeTargets,
  resolveSelectedIndex,
  getIncludedRecords,
} from "../merge/mergeRecordsHelpers";

/**
 * Pure decision logic behind the merge-opportunities drawer (EAI-1815).
 *
 * Split out of ``MergeOpportunityDrawer`` so the parts that decide *what gets
 * merged* — the default master, which records take part, which value wins each
 * field, and the resulting ``mergeLeadOpportunity`` payload — can be reasoned
 * about and exercised without a React tree.
 */

/**
 * "Sales / Used Vehicle" — the type/source hierarchy read outermost-parent
 * first. Both `opportunityType` and `opportunitySource` nest their ancestors
 * two levels deep under different key names, hence the `parentKey`.
 */
const hierarchyLabel = (node, parentKey) => {
  const names = [];
  let current = node;
  while (current?.name) {
    names.unshift(current.name);
    current = current[parentKey];
  }
  return names.join(" / ");
};

/**
 * "Brayden Evans (Salesperson - Primary Assigned)" for every owner on the
 * record. Mirrors the role composition the opportunity card already uses in
 * ``handleOwnerName`` so the drawer and the card agree.
 */
const ownersLabel = opportunityUsers =>
  (opportunityUsers || [])
    .map(item => {
      const companyRole = item?.user?.companyRole?.name;
      const userType = item?.userType?.name;
      const roleInfo = [companyRole, userType].filter(Boolean).join(" - ");
      return `${item?.user?.fullName || ""}${roleInfo ? ` (${roleInfo})` : ""}`.trim();
    })
    .filter(Boolean)
    .join(", ");

/**
 * "2014 Jeep Wrangler (Buy) · Stock #TB222383" per vehicle of interest.
 * ``getVehicleName`` supplies the required Year Make Model ordering and drops
 * the BE's "unknown" placeholders.
 */
const vehiclesLabel = vehiclesOfInterest =>
  getUniqueVehicleOfInterest(vehiclesOfInterest)
    .map(voi => {
      const name = getVehicleName(voi);
      const interest = voi?.customerInterest ? `(${voi.customerInterest})` : "";
      const stock = voi?.stockNumber ? `· Stock #${voi.stockNumber}` : "";
      return [name, interest, stock].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .join(", ");

/**
 * The mergeable fields, in design order. ``value`` renders the comparison cell;
 * ``variables`` contributes the winning record's slice of the
 * ``createLeadOpportunity`` payload, so a field's displayed value and the value
 * actually merged can never drift apart.
 */
export const MERGE_FIELDS = [
  {
    key: "name",
    sourceField: "name",
    langKey: "name",
    value: opp => opp?.name,
    variables: opp => ({ name: opp?.name }),
  },
  {
    key: "type",
    sourceField: "opportunity_type_id",
    langKey: "type",
    value: opp => hierarchyLabel(opp?.opportunityType, "parentType"),
    variables: opp => ({ opportunityTypeId: +opp?.opportunityTypeId }),
  },
  {
    key: "location",
    sourceField: "location_id",
    langKey: "location",
    value: opp => opp?.location?.title,
    variables: opp => ({ locationId: opp?.location?.id ? +opp.location.id : null }),
  },
  {
    key: "vehicle",
    langKey: "vehicle",
    value: opp => vehiclesLabel(opp?.vehiclesOfInterest),
    // Row ids, not payloads: the merge moves every vehicle onto the master and
    // then re-points the ones that lost the pick at an archived opportunity
    // (`lead_vehicle_of_interest` has no `is_active` to flip).
    variables: opp => ({
      keepVehicleIds: (opp?.vehiclesOfInterest || []).map(voi => +voi?.id).filter(Boolean),
    }),
  },
  {
    key: "owners",
    langKey: "owners",
    value: opp => ownersLabel(opp?.leadOpportunityUsers),
    // Row ids of the owners that stay active once the merge has moved them all
    // onto the master; the losers are deactivated, never deleted. Inactive rows
    // are filtered out so a merge can't resurrect a past assignment.
    variables: opp => ({
      keepOwnerIds: (opp?.leadOpportunityUsers || [])
        .filter(item => item?.isActive !== false)
        .map(item => +item?.id)
        .filter(Boolean),
    }),
  },
  {
    key: "source",
    sourceField: "opportunity_source_id",
    langKey: "source",
    value: opp => hierarchyLabel(opp?.opportunitySource, "parentSource"),
    variables: opp => ({ opportunitySourceId: +opp?.opportunitySourceId }),
  },
  {
    key: "status",
    sourceField: "opportunity_status_id",
    langKey: "status",
    value: opp => opp?.opportunityStatus?.name,
    variables: opp => ({ opportunityStatusId: +opp?.opportunityStatusId }),
  },
];

/**
 * Ids of the opportunities that should NOT get their own card, because a
 * visible anchor card already names them in its duplicate banner (EAI-1815).
 *
 * Never hides a record that would then have nothing on screen explaining
 * where it went:
 *  - one whose anchor isn't in `opportunities` (e.g. scoped out by the tab),
 *  - one that is itself an anchor — the BE's duplicate loader returns its
 *    results ordered by id DESC rather than in requested-key order, so a list
 *    can land on the wrong card and A and B can each name the other; keeping
 *    both is the safe resolution,
 *  - one flagged only by itself (same mis-attribution).
 *
 * @param {object[]} opportunities - the opportunities in view.
 * @returns {Set<number>} ids to leave out of the list.
 */
export const getHiddenDuplicateIds = opportunities => {
  const list = opportunities || [];

  const anchorIds = new Set();
  list.forEach(opp => {
    const owned = (opp?.potentialOpportunity || []).filter(id => +id !== +opp?.id);
    if (owned.length > 0) anchorIds.add(+opp?.id);
  });

  const visibleIds = new Set(list.map(opp => +opp?.id));
  const hidden = new Set();

  list.forEach(anchor => {
    if (!anchorIds.has(+anchor?.id)) return;
    (anchor?.potentialOpportunity || []).forEach(duplicateId => {
      const id = +duplicateId;
      // Self-reference, not in view, or an anchor in its own right.
      if (id === +anchor?.id || !visibleIds.has(id) || anchorIds.has(id)) return;
      hidden.add(id);
    });
  });

  return hidden;
};

/**
 * Which opportunity each winning field value came from — audit only (EAI-1871).
 *
 * The merge reads the values themselves from the arguments; this exists so the
 * audit log can answer "where did this value come from", which a before/after
 * diff cannot. `field` must be the API's snake_case column name: the server
 * looks it up in `MERGE_ARGUMENT_COLUMNS` and falls back to the raw string, so
 * a camelCase key would be recorded verbatim and match no column.
 *
 * Only scalar fields carry a `sourceField`. Owners and vehicles are sets of
 * rows rather than single values, so provenance is meaningless for them.
 */
export const buildValueSources = ({ records, primaryIdx, excluded, picks }) => {
  const included = getIncludedRecords(records, primaryIdx, excluded);
  return MERGE_FIELDS.filter(field => field.sourceField).map(field => {
    const index = resolveSelectedIndex({ picks, fieldKey: field.key, included, primaryIdx });
    return { field: field.sourceField, opportunityId: +records[index]?.id };
  });
};

/**
 * The full ``mergeLeadOpportunity`` payload for the current selection.
 *
 * The master is kept and updated in place, so it travels as its own argument
 * and is deliberately absent from ``mergeOpportunityIds`` — the API rejects a
 * request that lists it twice. Records the user marked "Do not merge" are left
 * out entirely, which is what keeps them live.
 */
export const buildMergeVariables = ({ records, primaryIdx, excluded, picks }) => ({
  ...getMergeTargets({ records, primaryIdx, excluded }),
  ...buildFieldVariables({ fields: MERGE_FIELDS, records, primaryIdx, excluded, picks }),
  valueSources: buildValueSources({ records, primaryIdx, excluded, picks }),
});
